import { FC } from 'react'
import { Relationship, User, UserFull } from '@wellesley/openapi'
import { Button, ButtonProps, cn } from '@wellesley/ui'

import { canUnfollow, isFollowPending, canFollowBack } from '@wellesley/user'
import useHover from 'hooks/useHover'
import useBlock from 'hooks/useBlock'

export type FollowButtonProps = {
  user: User | UserFull
  follow: () => void
  unfollow: () => void
  relationship: Relationship
  isRelationshipPending: boolean
  disabled?: boolean
  className?: string
  followButtonText?: string
  unfollowButtonText?: string
} & Pick<ButtonProps, 'size'>

const FollowButton: FC<FollowButtonProps> = ({
  size,
  user,
  follow,
  unfollow,
  disabled,
  relationship,
  isRelationshipPending,
  className,
  followButtonText,
  unfollowButtonText,
}) => {
  const [isBlocked, , unblock] = useBlock({ user, relationship })

  const followText = canFollowBack(relationship)
    ? 'Follow back'
    : followButtonText || 'Follow'

  const unfollowText = unfollowButtonText || 'Unfollow'

  const isPending = isFollowPending(relationship)
  const canUnfollowUser = canUnfollow(relationship)

  const [hoverRef, isHovered] = useHover<HTMLButtonElement>()

  const isPendingOrCanUnfollow = (isPending && !isHovered) || canUnfollowUser

  if (isRelationshipPending) return

  const testId = isBlocked
    ? 'unblock-button'
    : canUnfollowUser
      ? 'unfollow-button'
      : 'follow-button'

  return (
    <Button
      ref={hoverRef}
      data-testid={testId}
      onClick={
        isBlocked ? unblock : canUnfollowUser || isPending ? unfollow : follow
      }
      size={size}
      className={cn(
        'rounded-m px-xs w-[100%] min-w-[140px] md:max-w-[300px] lg:max-w-full',
        !canUnfollow && 'bg-gray-700',
        className,
      )}
      variant={
        isBlocked
          ? 'secondary_danger'
          : isPendingOrCanUnfollow
            ? 'secondary'
            : 'primary'
      }
      disabled={disabled}
    >
      {isBlocked
        ? 'Unblock'
        : isPending
          ? isHovered
            ? 'Cancel follow request'
            : 'Follow Pending'
          : canUnfollowUser
            ? unfollowText
            : followText}
    </Button>
  )
}

export default FollowButton
